import { ArrowLeft, RotateCcw } from "lucide-react";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import {
  AvailabilitySection, DetailsSection, PreviewSection, RulesSection, StaffSection,
} from "@/features/calendar-sections";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  TYPE_INFO, bodyFromForm, formFromCalendar, formProblems, sectionOf, type CalendarFormValues,
  type CalendarSection,
} from "@/lib/calendars";
import { sameValues } from "@/lib/form";
import { useLeaveGuard } from "@/lib/leave-guard";
import { useCalendar, useUpdateCalendar } from "@/lib/queries";
import { atLeast, type Calendar } from "@/lib/types";
import { cn } from "@/lib/utils";

const SECTIONS: { key: CalendarSection; label: string }[] = [
  { key: "details", label: "Basic details" },
  { key: "staff", label: "Staff & location" },
  { key: "availability", label: "Availability" },
  { key: "rules", label: "Booking rules" },
  { key: "preview", label: "Preview" },
];

/**
 * One calendar's settings (ACL-288), as GoHighLevel lays them out: the sections down the left,
 * one form, one Save. The API saves a calendar and its whole staff list in one `PATCH`, so the page
 * does too: a staff member's hours never land without the staff member, or the other way round.
 *
 * The section is in the URL (`?section=availability`) so a reload lands where the reader was. A
 * save the form can already see would be refused is not sent: it jumps to the first section with
 * a problem instead. Leaving with unsaved changes asks first. Supervisors read; admins change.
 */
export function CalendarPage() {
  const params = useParams();
  const id = Number(params.calendarId);
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const calendar = useCalendar(Number.isFinite(id) ? id : undefined);

  if (calendar.isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (calendar.error || !calendar.data) {
    const missing = calendar.error instanceof ApiError && calendar.error.status === 404;
    const back = (
      <Button variant="outline" asChild>
        <Link to="/calendars?tab=settings">Back to calendars</Link>
      </Button>
    );
    return (
      <Page>
        {missing ? (
          <EmptyState className="min-h-[60vh]" title="Calendar not found" description="It belongs to another organization, or never existed." action={back} />
        ) : (
          <ErrorState className="min-h-[60vh]" error={calendar.error} title="Could not load the calendar" action={back} />
        )}
      </Page>
    );
  }
  return <Editor key={calendar.data.id} calendar={calendar.data} canManage={canManage} />;
}

function Editor({ calendar, canManage }: { calendar: Calendar; canManage: boolean }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const requested = params.get("section") as CalendarSection | null;
  const section: CalendarSection = SECTIONS.some((s) => s.key === requested) ? requested! : "details";
  const readOnly = !canManage;

  const save = useUpdateCalendar();

  const [baseline, setBaseline] = React.useState<CalendarFormValues>(() => formFromCalendar(calendar));
  const [values, setValues] = React.useState<CalendarFormValues>(baseline);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const dirty = !sameValues(values, baseline);
  const problems = formProblems(values);
  const problemSections = new Set(Object.keys(problems).map(sectionOf));

  // Saved elsewhere while this page is open: taken in when nothing here is unsaved, never over it.
  const remote = React.useMemo(() => formFromCalendar(calendar), [calendar]);
  React.useEffect(() => {
    if (!dirty && !sameValues(remote, baseline)) {
      setBaseline(remote);
      setValues(remote);
    }
  }, [remote, baseline, dirty]);

  const update = React.useCallback((change: (v: CalendarFormValues) => CalendarFormValues) => {
    setValues((v) => change(v));
  }, []);

  const go = (next: CalendarSection) =>
    setParams(next === "details" ? {} : { section: next }, { replace: true });

  const submit = async () => {
    if (readOnly || save.isPending || !dirty) return;
    setSaveError(null);
    const first = SECTIONS.find((s) => problemSections.has(s.key));
    if (first) {
      go(first.key);
      return;
    }
    try {
      const saved = await save.mutateAsync({ id: calendar.id, ...bodyFromForm(values) });
      const kept = formFromCalendar(saved);
      setBaseline(kept);
      setValues(kept);
      toast.success("Calendar saved", saved.name);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Could not save the calendar. Please try again.");
    }
  };

  // Leaving with unsaved changes asks first, however the page is left (ACL-404).
  const { dialog: leaveDialog } = useLeaveGuard(dirty && !readOnly, "Your changes to this calendar have not been saved.");

  // ⌘S saves.
  const submitRef = React.useRef(submit);
  submitRef.current = submit;
  React.useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void submitRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readOnly]);

  const props = { values, update, problems, readOnly };

  return (
    <Page>
      <Link to="/calendars?tab=settings" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Calendars
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4 pb-5">
        <div className="min-w-0 space-y-1">
          <h1 className="inline-flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight text-text">
            {calendar.name}
            <Badge tone="accent">{TYPE_INFO[calendar.calendar_type].label}</Badge>
            {calendar.is_main && <Badge tone="success">Main</Badge>}
            {!calendar.active && <Badge tone="warning">Inactive</Badge>}
          </h1>
          <p className="text-sm text-text-muted">
            {readOnly ? "Only an admin can change a calendar." : dirty ? "Unsaved changes" : "All changes saved"}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={!dirty || save.isPending} onClick={() => setValues(baseline)}>
              <RotateCcw />
              Discard
            </Button>
            <Button disabled={!dirty} loading={save.isPending} onClick={() => void submit()}>
              Save changes
            </Button>
          </div>
        )}
      </header>

      {saveError && (
        <Callout tone="danger" className="mb-4">
          {saveError}
        </Callout>
      )}

      <div className="grid gap-6 md:grid-cols-[12rem_minmax(0,1fr)]">
        <nav aria-label="Calendar settings" className="md:sticky md:top-4 md:self-start">
          <ul className="flex gap-1 overflow-x-auto md:flex-col">
            {SECTIONS.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  aria-current={section === s.key ? "page" : undefined}
                  onClick={() => go(s.key)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors",
                    section === s.key ? "bg-accent-3 font-medium text-accent-11" : "text-text-muted hover:bg-gray-3 hover:text-text",
                  )}
                >
                  {s.label}
                  {problemSections.has(s.key) && <span className="size-2 rounded-full bg-danger-9" aria-label="Needs attention" />}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0">
          {section === "details" && <DetailsSection {...props} />}
          {section === "staff" && <StaffSection {...props} />}
          {section === "availability" && <AvailabilitySection {...props} />}
          {section === "rules" && <RulesSection {...props} />}
          {section === "preview" && <PreviewSection calendar={calendar} unsaved={dirty} />}
        </div>
      </div>

      {leaveDialog}
    </Page>
  );
}
